import { useFormik } from 'formik'
import Link from 'next/link'
import LoadingButton from '@mui/lab/LoadingButton'
import { Typography, TextField, Button, Stack } from '@mui/material'
import { useRouter } from 'next/router'

import { ForgotPasswordSchema, forgotInitialValues } from 'src/validationSchemas/loginSchema'
import useLogin from 'src/hooks/useLogin'
import VLAlert from 'src/components/common/VLAlert'
import theme from 'src/theme/theme'
import { useEffect, useState } from 'react'
import BrandBg from 'src/components/BrandBg'
import { Lock } from '@mui/icons-material'

export default function ForgotPassword() {
  const { forgotPassword, loading, message, setMessage, newPassword, setNewPassword, confirmPassword, setConfirmPassword, handlePassword } =
    useLogin()
  const { replace } = useRouter()
  const router = useRouter()
  const formik = useFormik({
    initialValues: forgotInitialValues,
    validationSchema: ForgotPasswordSchema,
    onSubmit: forgotPassword,
  })
  const [changePassword, setChangePassword] = useState<boolean>(false)
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search)
    const token = queryParams.get('token')
    if (token) {
      setChangePassword(true)
    }
  }, [])
  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="center" height="100vh">
        <BrandBg />
        <Stack id="right" flex={1.3} alignItems="center" justifyContent="center" padding={2}>
          <Stack
            component="form"
            id="form"
            padding={4}
            maxWidth="32rem"
            width="100%"
            borderRadius={2}
            gap={1}
            boxShadow="0 0 8px 2px #f6f6f9"
            border={`1px solid ${theme.palette.background.paper}`}
            onSubmit={formik.handleSubmit}
            noValidate
          >
            <Stack gap={2}>
              <Stack p={2} bgcolor={'rgba(12, 81, 119, 0.04)'} borderRadius={1} width={'fit-content'} justifyContent={'center'} alignItems={'center'}>
                <Lock fontSize="small" color="primary" />
              </Stack>
              <Typography variant="h5" fontWeight={700} color={theme.palette.primary.main}>
                Reset Password
              </Typography>
            </Stack>
            <Typography variant="body2">{`Enter your username/email address & we'll redirect you to a link to reset your password.`}</Typography>
            <Stack id="fields" gap={3} marginTop={2}>
              {!changePassword && (
                <TextField
                  required
                  autoFocus
                  autoComplete="off"
                  name="username"
                  id="username"
                  size="small"
                  placeholder="user@viewlift.com"
                  label="Username"
                  value={formik.values.username}
                  onChange={formik.handleChange}
                  error={Boolean(formik.touched.username && formik.errors.username)}
                  helperText={formik.touched.username && formik.errors.username}
                />
              )}
              {changePassword && (
                <>
                  <TextField
                    required
                    autoFocus
                    autoComplete="off"
                    name="newPassword"
                    id="newPassword"
                    size="small"
                    placeholder="New Password"
                    label="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    type="password"
                  />
                  <TextField
                    required
                    autoFocus
                    autoComplete="off"
                    name="confirmPassword"
                    id="confirmPassword"
                    size="small"
                    placeholder="Confirm Password"
                    label="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    type="password"
                  />
                </>
              )}
              <Stack gap={2}>
                {!changePassword && (
                  <>
                    <LoadingButton variant="contained" disableElevation loading={loading} type="submit">
                      Confirm Email
                    </LoadingButton>
                    <Button variant="text" color="primary" size="small" onClick={() => replace('/')}>
                      Back to Login
                    </Button>
                  </>
                )}
                {changePassword && (
                  <>
                    <LoadingButton variant="contained" disableElevation loading={loading} onClick={() => handlePassword()}>
                      Confirm
                    </LoadingButton>
                    <Button variant="text" color="primary" size="small" onClick={() => router.back()}>
                      Back to Previous Page
                    </Button>
                  </>
                )}
              </Stack>
              <Typography textAlign="center">
                Questions? Contact{' '}
                <Link style={{ color: theme.palette.primary.main, fontWeight: 600 }} href="mailto:techsupport@viewlift.com">
                  techsupport@viewlift.com
                </Link>
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <VLAlert message={message} setMessage={setMessage} success={false} />
    </>
  )
}
